import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useReferralPromptEligibility } from '../api/queries';

import { FeatureGates } from 'modules/common/constants/featuregates';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import { bottomRightPopupSession } from 'modules/common/components/Layout/LayoutContext/bottomRightPopupSession';
import useStatsigGate from 'modules/common/useStatsigGate';

const REFERRAL_PROMPT_DELAY_MS = 30_000;

export default function useReferralPromptDisplay() {
    const [shouldShow, setShouldShow] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasTriggeredRef = useRef(false);

    const { pathname } = useLocation();
    const gateEnabled = useStatsigGate(FeatureGates.ReferralPrompt);
    const { data: eligibility } = useReferralPromptEligibility(gateEnabled);
    const { activeBottomRightPopup } = useLayoutContext();
    const isDashboard = pathname.startsWith('/dashboard');

    useEffect(() => {
        if (!gateEnabled || !eligibility || isDashboard) {
            return undefined;
        }

        if (!eligibility.eligible) {
            datadogRum.addAction('referral_prompt_skipped', { reason: 'not_eligible' });
            return undefined;
        }
        if (activeBottomRightPopup !== null) {
            datadogRum.addAction('referral_prompt_skipped', {
                reason: 'other_popup_active',
                activePopup: activeBottomRightPopup,
            });
            return undefined;
        }
        if (bottomRightPopupSession.wasAnotherPopupDisplayed('referral')) {
            datadogRum.addAction('referral_prompt_skipped', { reason: 'another_popup_displayed_in_session' });
            return undefined;
        }
        if (hasTriggeredRef.current) {
            return undefined;
        }

        timerRef.current = setTimeout(() => {
            hasTriggeredRef.current = true;
            setShouldShow(true);
            bottomRightPopupSession.markAsDisplayed('referral');
            datadogRum.addAction('referral_prompt_displayed');
        }, REFERRAL_PROMPT_DELAY_MS);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [gateEnabled, eligibility, activeBottomRightPopup, isDashboard]);

    const dismiss = useCallback(() => {
        setShouldShow(false);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, []);

    return { shouldShow, dismiss };
}
